// utils/useSortableTable.js
import { useState } from 'react';
import { Box } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

/**
 * Custom hook for sortable table functionality
 * 
 * @param {Array} data - Original data array
 * @param {Object} columnConfig - Configuration for column types
 * @example
 * const columnConfig = {
 *   time: 'number',
 *   lastUpdate: 'date',
 *   unitName: 'string',
 *   ip: 'string'
 * }
 * 
 * @returns {Object} - Sorting utilities
 */
export const useSortableTable = (data, columnConfig = {}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  /**
   * Handle column sort with 3-state cycle: asc -> desc -> original
   * @param {string} key - Column key to sort by
   * @param {Array} currentData - Current filtered/displayed data
   * @returns {Array} - Sorted data
   */
  const handleSort = (key, currentData) => {
    let direction = 'asc';

    // Check if clicking the same column
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc';
      } else if (sortConfig.direction === 'desc') {
        // Reset to original order
        setSortConfig({ key: null, direction: null });
        return [...data]; // Return original data
      }
    }

    setSortConfig({ key, direction });

    // Perform sorting
    const sorted = [...currentData].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];

      // Determine column type
      const columnType = columnConfig[key] || 'string';

      switch (columnType) {
        case 'number':
          // Direct numeric comparison
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
          break;

        case 'date':
          // Convert to timestamp
          aValue = new Date(aValue).getTime() || 0;
          bValue = new Date(bValue).getTime() || 0;
          break;

        case 'string':
        default:
          // Case-insensitive string comparison
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
          break;
      }

      // Compare values
      if (aValue < bValue) {
        return direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  };

  /**
   * Get sort icon for a column
   * @param {string} columnKey - Column key to check
   * @returns {JSX.Element|null} - Sort icon or null
   */
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return null;
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
      
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
    );
  };

  /**
   * Get sortable header cell props
   * @param {string} columnKey - Column key
   * @param {string} label - Column label to display
   * @returns {Object} - Props for TableCell and content
   */
  const getSortableHeaderProps = (columnKey, label) => {
    return {
      onClick: () => ({ columnKey }), // Return key for parent to handle
      sx: {
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': { 
          opacity: 0.8,
          transition: 'opacity 0.2s'
        },
      },
      content: (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {label}
          {getSortIcon(columnKey)}
        </Box>
      )
    };
  };

  /**
   * Reset sorting to original order
   */
  const resetSort = () => {
    setSortConfig({ key: null, direction: null });
  };

  return {
    sortConfig,
    handleSort,
    getSortIcon,
    getSortableHeaderProps,
    resetSort,
    isSorted: sortConfig.key !== null,
  };
};

export default useSortableTable;